import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { UserRole, adminPath } from '@forever/shared';
import { Shell } from '@/components/layout/Shell';
import { useAuth } from '@/lib/auth';
import { SESSION_EXPIRED_EVENT } from '@/lib/api';
import { CallProvider } from '@/features/calls/CallProvider';
import { InvitationLinkDialog } from '@/features/invitations/InvitationLink';

/*
 * Sayfalar tembel yükleniyor: panelde yirmi ekran var ve bunların çoğuna
 * bir oturumda hiç girilmiyor. İlk açılış yalnızca gereken parçayı indirir.
 */
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ClientsPage = lazy(() =>
  import('@/pages/ClientsPage').then((m) => ({ default: m.ClientsPage })),
);
const ClientDetailPage = lazy(() =>
  import('@/pages/ClientDetailPage').then((m) => ({
    default: m.ClientDetailPage,
  })),
);
const NewClientPage = lazy(() =>
  import('@/pages/NewClientPage').then((m) => ({ default: m.NewClientPage })),
);
const PackagesPage = lazy(() =>
  import('@/pages/PackagesPage').then((m) => ({ default: m.PackagesPage })),
);
const PlansPage = lazy(() => import('@/pages/PlansPage').then((m) => ({ default: m.PlansPage })));
const PlanEditorPage = lazy(() =>
  import('@/pages/PlanEditorPage').then((m) => ({ default: m.PlanEditorPage })),
);
const ExchangeLibraryPage = lazy(() =>
  import('@/pages/ExchangeLibraryPage').then((m) => ({ default: m.ExchangeLibraryPage })),
);
const TemplatesPage = lazy(() =>
  import('@/pages/TemplatesPage').then((m) => ({ default: m.TemplatesPage })),
);
const MessagesPage = lazy(() =>
  import('@/pages/MessagesPage').then((m) => ({ default: m.MessagesPage })),
);
const QuestionsPage = lazy(() =>
  import('@/pages/QuestionsPage').then((m) => ({ default: m.QuestionsPage })),
);
const PhotosPage = lazy(() =>
  import('@/pages/PhotosPage').then((m) => ({ default: m.PhotosPage })),
);
const MeasurementsPage = lazy(() =>
  import('@/pages/MeasurementsPage').then((m) => ({
    default: m.MeasurementsPage,
  })),
);
const CalendarPage = lazy(() =>
  import('@/pages/CalendarPage').then((m) => ({ default: m.CalendarPage })),
);
const NotificationRulesPage = lazy(() =>
  import('@/pages/NotificationRulesPage').then((m) => ({
    default: m.NotificationRulesPage,
  })),
);
const ReportsPage = lazy(() =>
  import('@/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const TeamPage = lazy(() => import('@/pages/TeamPage').then((m) => ({ default: m.TeamPage })));
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const AuditPage = lazy(() => import('@/pages/AuditPage').then((m) => ({ default: m.AuditPage })));
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

/** Sayfa parçası inerken gösterilen ara durum. */
function PageFallback() {
  return (
    <div className="fd-page" aria-busy="true">
      <div className="fd-stack">
        <div className="fd-skeleton" style={{ height: 38, width: 260 }} />
        <div className="fd-grid fd-grid--4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="fd-skeleton" style={{ height: 104 }} />
          ))}
        </div>
        <div className="fd-skeleton" style={{ height: 320 }} />
      </div>
    </div>
  );
}

export function App() {
  const user = useAuth((s) => s.user);
  const initializing = useAuth((s) => s.initializing);
  const restore = useAuth((s) => s.restore);
  const navigate = useNavigate();

  useEffect(() => {
    void restore();
  }, [restore]);

  // Jeton yenilenemediğinde panel giriş ekranına döner.
  useEffect(() => {
    const onExpired = () => navigate('/giris', { replace: true });
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [navigate]);

  if (initializing) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Loader2 className="fd-spin" size={28} aria-label="Yükleniyor" />
      </div>
    );
  }

  if (!user) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/giris" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/giris" replace />} />
        </Routes>
      </Suspense>
    );
  }

  const isAdmin = user.role === UserRole.ADMIN;

  return (
    <CallProvider>
      <Shell>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path={adminPath.dashboard} element={<DashboardPage />} />

            <Route path={adminPath.clients} element={<ClientsPage />} />
            <Route path={adminPath.newClient} element={<NewClientPage />} />
            <Route path={`${adminPath.clients}/:id`} element={<ClientDetailPage />} />

            <Route path={adminPath.plans} element={<PlansPage />} />
            <Route path={`${adminPath.plans}/:id`} element={<PlanEditorPage />} />
            <Route path={adminPath.templates} element={<TemplatesPage />} />
          <Route path={adminPath.exchangeLibrary} element={<ExchangeLibraryPage />} />

            <Route path={adminPath.messages} element={<MessagesPage />} />
            <Route path={`${adminPath.messages}/:id`} element={<MessagesPage />} />
            <Route path={adminPath.questions} element={<QuestionsPage />} />

            <Route path={adminPath.photos} element={<PhotosPage />} />
            <Route path={`${adminPath.photos}/:id`} element={<PhotosPage />} />

            <Route path={adminPath.measurements} element={<MeasurementsPage />} />
            <Route path={adminPath.calendar} element={<CalendarPage />} />
            <Route path={adminPath.reports} element={<ReportsPage />} />

            {/* Yönetici bölümleri — sunucu tarafında da ayrıca korunuyor. */}
            <Route
              path={adminPath.packages}
              element={isAdmin ? <PackagesPage /> : <Navigate to={adminPath.dashboard} replace />}
            />
            <Route
              path={adminPath.notificationRules}
              element={
                isAdmin ? <NotificationRulesPage /> : <Navigate to={adminPath.dashboard} replace />
              }
            />
            <Route
              path={adminPath.team}
              element={isAdmin ? <TeamPage /> : <Navigate to={adminPath.dashboard} replace />}
            />
            <Route
              path={adminPath.settings}
              element={isAdmin ? <SettingsPage /> : <Navigate to={adminPath.dashboard} replace />}
            />
            <Route
              path={adminPath.audit}
              element={isAdmin ? <AuditPage /> : <Navigate to={adminPath.dashboard} replace />}
            />

            <Route path="/giris" element={<Navigate to={adminPath.dashboard} replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <InvitationLinkDialog />
        </Suspense>
      </Shell>
    </CallProvider>
  );
}
